import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, TextField, Button, IconButton, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Avatar, Dialog, DialogTitle, DialogContent, DialogActions,
  Stack, Grid, Card, CardContent, Alert, Collapse, Divider,
  useTheme, useMediaQuery, InputAdornment, Tooltip, Badge,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  Add as AddIcon, Remove as RemoveIcon, PlayArrow, Stop, Warning as WarningIcon,
  CheckCircle, ShoppingBag, ExpandMore, ExpandLess, Refresh, Search,
  TrendingUp, Inventory, Restaurant, LocalDining, Speed, FilterList,
  ShoppingCart, NoteAdd
} from '@mui/icons-material';
import { motion } from 'framer-motion';

import inventoryService from '../../services/inventoryService';
import ingredientsService from '../../services/ingredientsService';

// Create motion component
const MotionDiv = motion.div;

const useAlertDialog = () => ({
  alertState: { open: false },
  showSuccess: () => {},
  showError: () => {},
  closeAlert: () => {}
});

const AlertDialog = () => null;

const BasicSetupTab = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Alert dialog hook
  const { alertState, showSuccess, showError, closeAlert } = useAlertDialog();

  // State management
  const [loading, setLoading] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [recipeData, setRecipeData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [productionDialog, setProductionDialog] = useState({ open: false, item: null });
  const [ingredientDialog, setIngredientDialog] = useState({ open: false, item: null });

  const loadingRef = useRef(false);

  // Safe formatting functions
  const formatQuantity = useCallback((value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue === 0) return '';
    return Number.isInteger(numValue) ? numValue.toString() : numValue.toString();
  }, []);

  const formatCurrency = useCallback((value, decimals = 2) => {
    const numValue = parseFloat(value);
    return isNaN(numValue) ? '0.00' : numValue.toFixed(decimals);
  }, []);

  // Load all data
  const loadAllData = useCallback(async () => {
    if (loadingRef.current) return;
    
    loadingRef.current = true;
    setLoading(true);
    
    try {
      const [menuResponse, ingredientsResponse, recipeItemsResponse] = await Promise.allSettled([
        inventoryService.getStock(),
        ingredientsService.getIngredients(), 
        inventoryService.getRecipeItems()
      ]);

      if (menuResponse.status === 'fulfilled' && menuResponse.value?.success) {
        setMenuItems(menuResponse.value.data || []);
      }

      if (ingredientsResponse.status === 'fulfilled' && ingredientsResponse.value?.success) {
        setIngredients(ingredientsResponse.value.data || []);
      }

      if (recipeItemsResponse.status === 'fulfilled' && recipeItemsResponse.value?.success) {
        setRecipeData(recipeItemsResponse.value.data || []);
      }
    } catch (errorData) {
      console.error('Error loading data:', errorData);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  // FIXED: Calculate maximum producible quantity - corrected logic
  const calculateMaxProducible = useMemo(() => {
    return (menuItem, recipe, ingredientsList) => {
      if (!recipe?.ingredients || !ingredientsList.length) {
        return { maxQuantity: 0, limitingIngredients: [], canProduce: false, maxServings: 0 };
      }

      // IMPORTANT: Recipe should always be stored as 1 serving basis
      const yieldQuantity = 1; // Always 1 serving per recipe
      let maxServings = Infinity;
      const limitingIngredients = [];

      console.log(`🧮 Calculating production for ${menuItem.name}:`);
      console.log(`📊 Recipe has ${recipe.ingredients.length} ingredients`);

      for (const recipeIngredient of recipe.ingredients) {
        const ingredient = ingredientsList.find(ing => ing.ingredient_id === recipeIngredient.ingredient_id);
        
        if (!ingredient) {
          console.warn(`❌ Ingredient ${recipeIngredient.ingredient_id} not found`);
          limitingIngredients.push({
            name: recipeIngredient.ingredient_name || 'Unknown',
            required: recipeIngredient.quantity_per_yield,
            available: 0,
            missing: true
          });
          maxServings = 0;
          continue;
        }

        const available = parseFloat(ingredient.current_stock || 0);
        const requiredPerServing = parseFloat(recipeIngredient.quantity_per_yield || 0);
        
        console.log(`🥬 ${ingredient.name}: Available=${available}, Required per serving=${requiredPerServing}`);
        
        if (requiredPerServing > 0) {
          // How many servings can we make with available ingredient?
          const possibleServings = Math.floor(available / requiredPerServing);
          
          console.log(`📊 ${ingredient.name} can make ${possibleServings} servings`);
          
          // Find the limiting ingredient (minimum servings possible)
          if (possibleServings < maxServings) {
            maxServings = possibleServings;
            console.log(`🎯 ${ingredient.name} is now the limiting ingredient (${possibleServings} servings)`);
          }

          // Check for shortages
          if (available < requiredPerServing) {
            limitingIngredients.push({
              name: ingredient.name,
              required: requiredPerServing,
              available: available,
              shortage: requiredPerServing - available,
              unit: ingredient.unit_of_measure || ingredient.package_uom || 'kg'
            });
          }
        }
      }

      // Ensure maxServings is never Infinity or negative
      const actualMaxServings = maxServings === Infinity ? 0 : Math.max(0, maxServings);
      
      console.log(`✅ Final calculation: ${actualMaxServings} servings possible`);

      return {
        maxQuantity: actualMaxServings, // This is the number of servings we can make
        maxServings: actualMaxServings, // Same as maxQuantity for clarity
        limitingIngredients,
        canProduce: actualMaxServings > 0,
        yieldQuantity: yieldQuantity
      };
    };
  }, []);

  // Enhanced menu items with production info
  const enhancedMenuItems = useMemo(() => {
    if (!menuItems.length) return [];
    
    return menuItems.map(menuItem => {
      const recipe = recipeData.find(r => r.item_id === menuItem.item_id);
      const productionInfo = calculateMaxProducible(menuItem, recipe, ingredients);
      
      // Get category info for styling
      const getCategoryColor = () => {
        if (!recipe) return theme.palette.grey[500];
        
        const ingredientTypes = recipe.ingredients?.map(ing => 
          ingredients.find(i => i.ingredient_id === ing.ingredient_id)?.type
        ).filter(Boolean);
        
        const hasRaw = ingredientTypes?.includes('raw');
        const hasPremade = ingredientTypes?.includes('premade');
        
        if (hasRaw && hasPremade) return theme.palette.warning.main;
        if (hasRaw) return theme.palette.info.main;
        if (hasPremade) return theme.palette.success.main;
        return theme.palette.grey[500];
      };

      const getCategoryLabel = () => {
        if (!recipe) return t('menu.uncategorized');
        
        const ingredientTypes = recipe.ingredients?.map(ing => 
          ingredients.find(i => i.ingredient_id === ing.ingredient_id)?.type
        ).filter(Boolean);
        
        const hasRaw = ingredientTypes?.includes('raw');
        const hasPremade = ingredientTypes?.includes('premade');
        
        if (hasRaw && hasPremade) return 'Mixed Recipe';
        if (hasRaw) return t('inventory.filters.raw');
        if (hasPremade) return t('inventory.filters.premade');
        return t('menu.uncategorized');
      };
      
      return {
        ...menuItem,
        recipe,
        hasRecipe: !!recipe,
        categoryColor: getCategoryColor(),
        categoryLabel: getCategoryLabel(),
        ...productionInfo
      };
    });
  }, [menuItems, recipeData, ingredients, calculateMaxProducible, theme, t]);

  // Filter items
  const filteredItems = useMemo(() => {
    let filtered = enhancedMenuItems;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.name?.toLowerCase().includes(term)
      );
    }

    if (categoryFilter !== 'all') {
      if (categoryFilter === 'with_recipes') {
        filtered = filtered.filter(item => item.hasRecipe);
      } else if (categoryFilter === 'can_produce') {
        filtered = filtered.filter(item => item.canProduce);
      } else if (categoryFilter === 'shortages') {
        filtered = filtered.filter(item => item.limitingIngredients.length > 0);
      }
    }

    return filtered.sort((a, b) => {
      if (a.canProduce && !b.canProduce) return -1;
      if (!a.canProduce && b.canProduce) return 1;
      return a.name?.localeCompare(b.name) || 0;
    });
  }, [enhancedMenuItems, searchTerm, categoryFilter]);

  // Toggle row expansion
  const toggleRowExpansion = useCallback((itemId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  // Handle production
  const handleStartProduction = useCallback((item, maxServings) => {
    setProductionDialog({ open: true, item, maxServings }); 
  }, []);

  const handleConfirmProduction = useCallback(async (item, quantity) => {
    try {
      setLoading(true);
      
      await inventoryService.createProductionRecord({
        date: new Date().toISOString().split('T')[0],
        product_id: item.item_id,
        quantity_produced: quantity,
        notes: `Production batch - ${quantity} servings`
      });

      showSuccess(t('inventory.setup.stockSaved', { item: item.name }));
      setProductionDialog({ open: false, item: null });
      loadAllData();
      
    } catch (errorData) {
      showError('Failed to create production: ' + errorData.message);
    } finally {
      setLoading(false);
    }
  }, [t, showSuccess, showError, loadAllData]); // Add showSuccess and showError here

  // Handle manual ingredient addition
  const handleManualIngredients = useCallback((item) => {
    setIngredientDialog({ open: true, item });
  }, []);

  const handleConfirmIngredientAdd = useCallback(async (item, additions, notes) => {
    try {
      setLoading(true);
      
      for (const addition of additions) {
        await ingredientsService.adjustIngredientStock(addition.ingredient_id, {
          physical_count: addition.currentStock + addition.addQuantity,
          reason: 'manual_purchase',
          notes: notes || 'Manual ingredient addition'
        });
      }

      showSuccess('Ingredients added successfully');
      setIngredientDialog({ open: false, item: null });
      loadAllData();
      
    } catch (errorData) {
      showError('Failed to add ingredients: ' + errorData.message);
    } finally {
      setLoading(false);
    }
  }, [showSuccess, showError, loadAllData]); // Add showSuccess and showError here

  // FIXED: Stock adjustment handlers for menu items
  const adjustStock = useCallback(async (item, delta) => {
    const currentQuantity = parseFloat(item.current_quantity || 0);
    const newQuantity = Math.max(0, currentQuantity + delta);
    
    try {
      setLoading(true);
      console.log(`📝 Adjusting menu item stock for ${item.name}: ${currentQuantity} → ${newQuantity}`);
      
      await inventoryService.saveSetup({
        stockData: [{
          item_id: item.item_id,
          current_quantity: newQuantity,
          min_quantity: item.min_quantity || 0,
          max_quantity: item.max_quantity || 100,
          unit_of_measure: item.unit_of_measure || 'pcs',
          cost_per_unit: item.cost_per_unit || 0
        }]
      });
      
      showSuccess(`Stock adjusted: ${item.name} now has ${newQuantity} units available for sale`);
      loadAllData();
    } catch (errorData) {
      console.error('❌ Stock adjustment failed:', errorData);
      showError('Failed to adjust stock: ' + (errorData.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [showError, showSuccess, loadAllData]); // Add showSuccess and showError here

  // Production with ingredient consumption
  const handleProduceWithIngredients = useCallback(async (item, quantity) => {
    if (!item.recipe?.ingredients) {
      showError('No recipe found for this item');
      return;
    }

    try {
      setLoading(true);

      // Calculate total ingredient needs
      const ingredientNeeds = item.recipe.ingredients.map(recipeIng => {
        const ingredient = ingredients.find(ing => ing.ingredient_id === recipeIng.ingredient_id);
        const totalNeeded = parseFloat(recipeIng.quantity_per_yield) * quantity;
        
        return {
          ingredient_id: recipeIng.ingredient_id,
          name: ingredient?.name || recipeIng.ingredient_name,
          needed: totalNeeded,
          available: ingredient?.current_stock || 0,
          sufficient: (ingredient?.current_stock || 0) >= totalNeeded
        };
      });

      // Check if we have enough ingredients
      const insufficientIngredients = ingredientNeeds.filter(need => !need.sufficient);
      if (insufficientIngredients.length > 0) {
        showError(`Insufficient ingredients: ${insufficientIngredients.map(ing => ing.name).join(', ')}`);
        return;
      }

      // Create production transaction
      const productionData = {
        menu_item_id: item.item_id,
        quantity_produced: quantity,
        ingredient_consumption: ingredientNeeds.map(need => ({
          ingredient_id: need.ingredient_id,
          quantity_consumed: need.needed
        }))
      };

      // Call backend API to handle the entire transaction
      await inventoryService.createProductionTransaction(productionData);
      
      showSuccess(`Successfully produced ${quantity} servings of ${item.name}`);
      loadAllData(); // Refresh all data

    } catch (error) {
      console.error('Production failed:', error);
      showError('Production failed: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [ingredients, showError, showSuccess, loadAllData]);

  // Revert production (add ingredients back)
  const handleRevertProduction = useCallback(async (item, quantity) => {
    if (!item.recipe?.ingredients) {
      showError('No recipe found for this item');
      return;
    }

    if ((item.current_quantity || 0) < quantity) {
      showError(`Cannot revert ${quantity} servings. Only ${item.current_quantity || 0} available.`);
      return;
    }

    try {
      setLoading(true);

      // Calculate ingredient returns
      const ingredientReturns = item.recipe.ingredients.map(recipeIng => ({
        ingredient_id: recipeIng.ingredient_id,
        quantity_returned: parseFloat(recipeIng.quantity_per_yield) * quantity
      }));

      const revertData = {
        menu_item_id: item.item_id,
        quantity_reverted: quantity,
        ingredient_returns: ingredientReturns
      };

      // Call backend API to handle the reversion transaction
      await inventoryService.revertProductionTransaction(revertData);
      
      showSuccess(`Successfully reverted ${quantity} servings of ${item.name}`);
      loadAllData(); // Refresh all data

    } catch (error) {
      console.error('Revert failed:', error);
      showError('Revert failed: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [showError, showSuccess, loadAllData]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Render ingredient shortages
  const renderShortages = useCallback((item) => {
    if (!item.limitingIngredients?.length) return null;

    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="error" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
          {t('ingredients.recipe.stockWarnings')}:
        </Typography>
        {item.limitingIngredients.slice(0, 2).map((ing, idx) => (
          <Typography key={idx} variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
            • {ing.name}: Need {formatQuantity(ing.required)}, Have {formatQuantity(ing.available)}
          </Typography>
        ))}
        {item.limitingIngredients.length > 2 && (
          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
            +{item.limitingIngredients.length - 2} more...
          </Typography>
        )}
      </Box>
    );
  }, [t, formatQuantity]);

  // Main table row component
  const TableRowComponent = useCallback(({ item }) => {
    const isExpanded = expandedRows.has(item.item_id);
    
    return (
      <React.Fragment key={item.item_id}>
        <TableRow
          hover
          sx={{
            borderLeft: `4px solid ${item.categoryColor}`,
            '& .MuiTableCell-root': {
              borderColor: theme.palette.divider,
              py: 1.5
            }
          }}
        >
          {/* Item Info */}
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                sx={{
                  bgcolor: item.categoryColor,
                  width: 32,
                  height: 32
                }}
              >
                {item.hasRecipe ? <Restaurant fontSize="small" /> : <LocalDining fontSize="small" />}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {item.name}
                </Typography>
                <Chip
                  size="small"
                  label={item.categoryLabel}
                  sx={{
                    bgcolor: item.categoryColor + '20',
                    color: item.categoryColor,
                    fontSize: '0.65rem',
                    height: '20px'
                  }}
                />
              </Box>
            </Box>
          </TableCell>

          {/* Current Stock */}
          <TableCell align="center">
            <Stack direction="row" spacing={1} justifyContent="center">
              {/* Simple stock adjustment (for pre-made items without recipes) */}
              {!item.hasRecipe && (
                <>
                  <Button
                    size="small"
                    startIcon={<RemoveIcon />}
                    onClick={() => adjustStock(item, -1)}
                    disabled={loading || (item.current_quantity || 0) <= 0}
                    color="error"
                    variant="outlined"
                  >
                    -1
                  </Button>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => adjustStock(item, 1)}
                    disabled={loading}
                    color="success"
                    variant="outlined"
                  >
                    +1
                  </Button>
                </>
              )}

              {/* Production controls (for items with recipes) */}
              {item.hasRecipe && (
                <>
                  <Button
                    size="small"
                    startIcon={<PlayArrow />}
                    onClick={() => handleProduceWithIngredients(item, 1)}
                    disabled={loading || !item.canProduce}
                    color="success"
                    variant="contained"
                  >
                    Produce 1
                  </Button>
                  
                  <Button
                    size="small"
                    startIcon={<Stop />}
                    onClick={() => handleRevertProduction(item, 1)}
                    disabled={loading || (item.current_quantity || 0) <= 0}
                    color="warning"
                    variant="outlined"
                  >
                    Revert 1
                  </Button>
                </>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {item.unit_of_measure || 'pcs'}
            </Typography>
          </TableCell>

          {/* FIXED: Production Capacity */}
          <TableCell align="center">
            {item.hasRecipe ? (
              <Box>
                <Typography variant="body2" sx={{ 
                  fontWeight: 700,
                  color: item.canProduce ? theme.palette.success.main : theme.palette.error.main
                }}>
                  {formatQuantity(item.maxServings)} servings
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  max producible
                </Typography>
              </Box>
            ) : (
              <Chip
                size="small"
                label="Pre-made"
                color="default"
                sx={{ fontSize: '0.7rem' }}
              />
            )}
          </TableCell>

          {/* Status & Actions */}
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {item.hasRecipe && (
                <>
                  {item.canProduce ? (
                    <Chip
                      icon={<CheckCircle fontSize="small" />}
                      label="Ready"
                      color="success"
                      size="small"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  ) : (
                    <Chip
                      icon={<WarningIcon fontSize="small" />}
                      label="Shortage"
                      color="warning"
                      size="small"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  )}
                  
                  <Tooltip title={item.canProduce ? "Start Production" : "Add Ingredients First"}>
                    <IconButton
                      size="small"
                      onClick={() => item.canProduce ? handleStartProduction(item, item.maxServings) : handleManualIngredients(item)}
                      color={item.canProduce ? "success" : "warning"}
                    >
                      {item.canProduce ? <PlayArrow /> : <ShoppingBag />}
                    </IconButton>
                  </Tooltip>
                </>
              )}
              
              <Tooltip title="Show Details">
                <IconButton
                  size="small"
                  onClick={() => toggleRowExpansion(item.item_id)}
                >
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Tooltip>
            </Box>
          </TableCell>

          {/* Price & Value */}
          <TableCell align="right">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              ${formatCurrency(item.price || item.selling_price)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              selling price
            </Typography>
          </TableCell>
        </TableRow>

        {/* FIXED: Expanded Row with corrected analysis */}
        {isExpanded && (
          <TableRow>
            <TableCell colSpan={5} sx={{ py: 2, bgcolor: theme.palette.background.default }}>
              <Collapse in={isExpanded}>
                <Box sx={{ px: 2 }}>
                  {item.hasRecipe && item.recipe?.ingredients ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          Recipe Ingredients ({item.recipe.ingredients.length})
                        </Typography>
                        <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                          {item.recipe.ingredients.map((ing, idx) => {
                            const ingredient = ingredients.find(i => i.ingredient_id === ing.ingredient_id);
                            const available = ingredient?.current_stock || 0;
                            const required = ing.quantity_per_yield || 0;
                            const sufficient = available >= required;

                            return (
                              <Box
                                key={idx}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  p: 1,
                                  mb: 0.5,
                                  bgcolor: sufficient ? theme.palette.success.light + '20' : theme.palette.error.light + '20',
                                  borderRadius: 1,
                                  border: `1px solid ${sufficient ? theme.palette.success.main : theme.palette.error.main}30`
                                }}
                              >
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {ingredient?.name || ing.ingredient_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Need: {formatQuantity(required)} | Have: {formatQuantity(available)}
                                  </Typography>
                                </Box>
                                <Chip
                                  size="small"
                                  icon={sufficient ? <CheckCircle fontSize="small" /> : <WarningIcon fontSize="small" />}
                                  label={sufficient ? 'OK' : 'Short'}
                                  color={sufficient ? 'success' : 'error'}
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          Production Analysis
                        </Typography>
                        <Stack spacing={1}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: theme.palette.info.light + '20', borderRadius: 1 }}>
                            <Typography variant="caption">Recipe Base:</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              1 serving per recipe
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: theme.palette.success.light + '20', borderRadius: 1 }}>
                            <Typography variant="caption">Max Servings:</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              {item.maxServings || 0}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: theme.palette.primary.light + '20', borderRadius: 1 }}>
                            <Typography variant="caption">Total Producible:</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              {item.maxServings} servings
                            </Typography>
                          </Box>
                        </Stack>
                        
                        {item.limitingIngredients.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Alert severity="warning" sx={{ fontSize: '0.75rem' }}>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                Ingredient Shortages:
                              </Typography>
                              {renderShortages(item)}
                            </Alert>
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      {t('ingredients.recipe.noRecipe')} - Configure recipe to enable production control
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  }, [expandedRows, theme, formatQuantity, formatCurrency, adjustStock, loading, handleStartProduction, handleManualIngredients, toggleRowExpansion, ingredients, renderShortages, t]);

  // Mobile card component
  const MobileCardComponent = useCallback(({ item }) => (
    <Card
      key={item.item_id}
      sx={{
        mb: 2,
        borderLeft: `4px solid ${item.categoryColor}`,
        '&:hover': { boxShadow: theme.shadows[4] }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
          <Avatar sx={{ bgcolor: item.categoryColor, width: 40, height: 40 }}>
            {item.hasRecipe ? <Restaurant /> : <LocalDining />}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5 }}>
              {item.name}
            </Typography>
            <Chip
              size="small"
              label={item.categoryLabel}
              sx={{
                bgcolor: item.categoryColor + '20',
                color: item.categoryColor,
                fontSize: '0.7rem'
              }}
            />
          </Box>
          <Typography variant="h6" color="primary">
            ${formatCurrency(item.price || item.selling_price)}
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 1, bgcolor: theme.palette.info.light + '20', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">Current Stock</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {formatQuantity(item.current_quantity || 0)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 1, bgcolor: item.canProduce ? theme.palette.success.light + '20' : theme.palette.error.light + '20', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {item.hasRecipe ? 'Can Produce' : 'Type'}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: item.hasRecipe ? (item.canProduce ? theme.palette.success.main : theme.palette.error.main) : 'inherit' }}>
                {item.hasRecipe ? `${formatQuantity(item.maxServings)} servings` : 'Pre-made'}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Stack direction="row" spacing={1} justifyContent="center">
          <Button
            size="small"
            startIcon={<RemoveIcon />}
            onClick={() => adjustStock(item, -1)}
            disabled={loading || (item.current_quantity || 0) <= 0}
            color="error"
            variant="outlined"
          >
            -1
          </Button>
          
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => adjustStock(item, 1)}
            disabled={loading}
            color="success"
            variant="outlined"
          >
            +1
          </Button>

          {item.hasRecipe && (
            <Button
              size="small"
              startIcon={item.canProduce ? <PlayArrow /> : <ShoppingBag />}
              onClick={() => item.canProduce ? handleStartProduction(item, item.maxServings) : handleManualIngredients(item)}
              color={item.canProduce ? "success" : "warning"}
              variant="contained"
            >
              {item.canProduce ? 'Produce' : 'Add Ingredients'}
            </Button>
          )}
        </Stack>

        {item.limitingIngredients?.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ fontSize: '0.75rem' }}>
              {renderShortages(item)}
            </Alert>
          </Box>
        )}
      </CardContent>
    </Card>
  ), [theme, formatQuantity, formatCurrency, adjustStock, loading, handleStartProduction, handleManualIngredients, renderShortages]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: theme.palette.primary.lighter || 'rgba(25, 118, 210, 0.1)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: theme.palette.primary.main }}>
                Production Control
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage production based on recipe capacity and ingredient availability
              </Typography>
            </Box>
            <Button
              startIcon={<Refresh />}
              onClick={loadAllData}
              disabled={loading}
              variant="outlined"
            >
              {t('common.refresh')}
            </Button>
          </Box>
        </Paper>
      </MotionDiv>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('common.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label="Filter"
                startAdornment={<FilterList fontSize="small" sx={{ mr: 1 }} />}
              >
                <MenuItem value="all">All Items ({filteredItems.length})</MenuItem>
                <MenuItem value="can_produce">
                  <Badge badgeContent={filteredItems.filter(i => i.canProduce).length} color="success">
                    Ready to Produce
                  </Badge>
                </MenuItem>
                <MenuItem value="shortages">
                  <Badge badgeContent={filteredItems.filter(i => i.limitingIngredients.length > 0).length} color="warning">
                    With Shortages
                  </Badge>
                </MenuItem>
                <MenuItem value="with_recipes">With Recipes</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Content */}
      {loading && filteredItems.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">Loading production data...</Typography>
        </Box>
      ) : filteredItems.length > 0 ? (
        <>
          {/* Desktop Table */}
          {!isMobile ? (
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                    <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Current Stock</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Production Capacity</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status & Actions</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Selling Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredItems.map((item) => (
                    <TableRowComponent key={item.item_id} item={item} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            /* Mobile Cards */
            <Box>
              {filteredItems.map((item) => (
                <MobileCardComponent key={item.item_id} item={item} />
              ))}
            </Box>
          )}
        </>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Inventory sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>No items found</Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm || categoryFilter !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'No menu items available for production'}
          </Typography>
        </Paper>
      )}

      {/* Production Dialog */}
      <ProductionDialog
        open={productionDialog.open}
        item={productionDialog.item}
        onClose={() => setProductionDialog({ open: false, item: null })}
        onConfirm={handleConfirmProduction}
        loading={loading}
      />

      {/* NEW: Enhanced Ingredient Dialog with Manual Addition */}
      <EnhancedIngredientDialog
        open={ingredientDialog.open}
        item={ingredientDialog.item}
        ingredients={ingredients}
        onClose={() => setIngredientDialog({ open: false, item: null })}
        onConfirm={handleConfirmIngredientAdd}
        loading={loading}
      />

      {/* Alert Dialog */}
      <AlertDialog
        open={alertState.open}
        onClose={closeAlert}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
      />
    </Box>
  );
};

// FIXED: Production Dialog Component with corrected calculations
const ProductionDialog = ({ open, item, onClose, onConfirm, loading }) => {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (item?.maxServings) {
      setQuantity(Math.min(item.maxServings, 1));
    }
  }, [item]);

  const handleConfirm = () => {
    if (item && quantity > 0) {
      onConfirm(item, quantity); // quantity is already in servings
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlayArrow color="success" />
          Start Production - {item?.name}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {item && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Recipe base: 1 serving per recipe<br/>
                Maximum servings possible: {item.maxServings}<br/>
                Each serving uses the ingredient quantities defined in the recipe
              </Alert>
              
              <TextField
                label="Number of Servings to Produce"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Math.min(item.maxServings, parseInt(e.target.value) || 1)))}
                inputProps={{ min: 1, max: item.maxServings }}
                fullWidth
                sx={{ mb: 2 }}
                helperText={`Each serving follows the recipe quantities`}
              />
              
              <Box sx={{ p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.main' }}>
                  Production Summary:
                </Typography>
                <Typography variant="body2">
                  Will produce {quantity} servings of {item.name}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          color="success"
          disabled={loading || !quantity || quantity > (item?.maxServings || 0)}
        >
          Start Production
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// NEW: Enhanced Ingredient Dialog with Manual Addition Feature
const EnhancedIngredientDialog = ({ open, item, ingredients, onClose, onConfirm, loading }) => {
  const { t } = useTranslation();
  const [ingredientAdditions, setIngredientAdditions] = useState([]);
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (item?.limitingIngredients && open) {
      const additions = item.limitingIngredients.map(limiting => {
        const ingredient = ingredients.find(ing => ing.name === limiting.name);
        return {
          ingredient_id: ingredient?.ingredient_id,
          name: limiting.name,
          currentStock: limiting.available || 0,
          shortage: limiting.shortage || 0,
          addQuantity: limiting.shortage || 0,
          unit: limiting.unit || 'kg'
        };
      }).filter(add => add.ingredient_id);
      
      setIngredientAdditions(additions);
      setNotes('');
      setReason('');
    }
  }, [item, ingredients, open]);

  const handleQuantityChange = (index, value) => {
    setIngredientAdditions(prev => 
      prev.map((item, i) => 
        i === index ? { ...item, addQuantity: Math.max(0, parseFloat(value) || 0) } : item
      )
    );
  };

  const handleConfirm = () => {
    const validAdditions = ingredientAdditions.filter(add => add.addQuantity > 0);
    if (validAdditions.length > 0 && reason && notes) {
      onConfirm(item, validAdditions, `${reason}: ${notes}`);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingCart color="warning" />
          Manual Ingredient Addition - {item?.name}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Add ingredients manually when you purchase them ad-hoc from nearby markets or suppliers.
            This will update your ingredient stock to enable production.
          </Alert>

          {/* Reason Selection */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Reason for Addition</InputLabel>
                <Select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  label="Reason for Addition"
                >
                  <MenuItem value="emergency_purchase">Emergency Purchase</MenuItem>
                  <MenuItem value="nearby_market">Nearby Market</MenuItem>
                  <MenuItem value="supplier_delivery">Supplier Delivery</MenuItem>
                  <MenuItem value="stock_correction">Stock Correction</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="How/Where did you get these ingredients?"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., 'Bought from ABC Market down the street due to urgent order'"
                required
              />
            </Grid>
          </Grid>

          <Stack spacing={2}>
            {ingredientAdditions.map((addition, index) => (
              <Paper key={index} sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                    {addition.name}
                  </Typography>
                  <Chip 
                    size="small" 
                    label={`Short: ${addition.shortage} ${addition.unit}`} 
                    color="error"
                  />
                </Box>
                
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'info.lighter', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Current Stock
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {addition.currentStock} {addition.unit}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'error.lighter', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Needed
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                        +{addition.shortage} {addition.unit}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Add Quantity"
                      type="number"
                      size="small"
                      value={addition.addQuantity}
                      onChange={(e) => handleQuantityChange(index, e.target.value)}
                      inputProps={{ min: 0, step: 0.001 }}
                      fullWidth
                      helperText={`Min: ${addition.shortage}`}
                    />
                  </Grid>
                </Grid>

                {/* Show result */}
                <Box sx={{ mt: 1, p: 1, bgcolor: 'success.lighter', borderRadius: 1 }}>
                  <Typography variant="caption" color="success.main">
                    After addition: {(addition.currentStock + addition.addQuantity).toFixed(3)} {addition.unit}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          color="warning"
          disabled={loading || ingredientAdditions.every(add => add.addQuantity <= 0) || !reason || !notes}
          startIcon={<NoteAdd />}
        >
          Add Ingredients & Update Stock
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BasicSetupTab;